import { Injectable } from '@angular/core';
import moment from 'moment';
import { Moment } from 'moment';
import { StoreWorkingTime } from '../../classes/models/store/StoreWorkingTime';
import { asDayOfWeek } from '../../classes/enums/DayOfWeek';
import { StoreDetails } from '../../classes/models/store/StoreDetails';
import { StoreDefaultWorkingTime } from '../../classes/models/store/StoreDefaultWorkingTime';

/**
 * This service calculates store working hours.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkingHoursService {

  /** Extra time in minutes before opening and after closing the store. */
  readonly EXTRA_TIME = 15;

  /**
   * Get a list of store opening times (with extra time added before opening a store) for a specific week. Special working times are
   * taken into account.
   * @param weekStart the Monday of the week, from which working times starts.
   * @param storeDetails information about the store.
   */
  getStoreOpeningTimesStartingAt(weekStart: string, storeDetails: StoreDetails): Moment[] {
    return this.getStoreTimesStartingAt(storeWorkingTime => storeWorkingTime.openingTime, weekStart, storeDetails)
      .map(item => item.subtract(this.EXTRA_TIME, 'minutes'));
  }

  /**
   * Get a list of store closing times (with extra time added after closing a store) for a specific week. Special working times are
   * taken into account.
   * @param weekStart the Monday of the week, from which working times starts.
   * @param storeDetails information about the store.
   */
  getStoreClosingTimesStartingAt(weekStart: string, storeDetails: StoreDetails): Moment[] {
    return this.getStoreTimesStartingAt(storeWorkingTime => storeWorkingTime.closingTime, weekStart, storeDetails)
      .map(item => item.add(this.EXTRA_TIME, 'minutes'));
  }

  private getStoreTimesStartingAt(
    timeExtractor: (storeWorkingTime: StoreWorkingTime) => string,
    date: string,
    storeDetails: StoreDetails
  ): Moment[] {
    const times: Moment[] = [];
    const weekStart = moment(date).startOf('week').isoWeekday(1);
    const weekEnd = moment(weekStart).add(6, 'days');
    for (const day = weekStart; day <= weekEnd; day.add(1, 'days')) {
      const specialTimeForDate = storeDetails.specialWorkingTimes
        .find(workingTime => workingTime.startDate === day.format('YYYY-MM-DD'));
      const defaultWorkingTimes = this.getDefaultWorkingTimesForSelectedWeek(date, storeDetails.defaultWorkingTimes);
      if (specialTimeForDate) {
        times.push(moment(timeExtractor(specialTimeForDate.workingTime), 'HH:mm:ss'));
      } else if (defaultWorkingTimes && defaultWorkingTimes.weeklyTimes.get(asDayOfWeek(day))) {
        times.push(moment(timeExtractor(defaultWorkingTimes.weeklyTimes.get(asDayOfWeek(day))), 'HH:mm:ss'));
      }
    }
    return times;
  }

  /**
   * Gets each hour, formatted in HH:00:00, between store opening and closing times.
   * @param fromTime store opening time.
   * @param toTime store closing time.
   */
  calculateHoursRange(fromTime: Moment, toTime: Moment): string[] {
    const hours = [];
    for (const i = fromTime.clone().startOf('hour'); i <= toTime.clone().startOf('hour'); i.add(1, 'hours')) {
      hours.push(i.format('HH:00:00'));
    }
    if (toTime.minutes() > 0) {
      hours.push(toTime.clone().add(1, 'hours').format('HH:00:00'));
    }
    return hours;
  }

  /**
   * Finds default working times with the latest start date.
   * @param workingTimes default store working times for multiple weeks.
   */
  getLatestDefaultWorkingTime(workingTimes: StoreDefaultWorkingTime[]): StoreDefaultWorkingTime | undefined {
    if (workingTimes.length === 0) {
      return undefined;
    }
    return workingTimes
      .reduce((previous, current) => current > previous ? current : previous);
  }

  private getDefaultWorkingTimesForSelectedWeek(
    weekStart: string,
    workingTimes: StoreDefaultWorkingTime[]
  ): StoreDefaultWorkingTime | undefined {
    const filteredWorkingTimes = workingTimes.filter(item => item.startDate <= weekStart);
    if (filteredWorkingTimes.length === 0) {
      return undefined;
    }
    return filteredWorkingTimes
      .reduce((previous, current) => current > previous ? current : previous);
  }
}
